import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["cancelLabel", "editLabel", "invalid", "invalidLabel", "onCancel", "onChange", "onSave", "readOnly", "saveLabel", "value"];
import React, { useState, useEffect, forwardRef, useRef } from 'react';
import { Button } from 'carbon-components-react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Edit24, Checkmark24, Close24, EditOff24, WarningFilled16 } from '@carbon/icons-react';
import { pkg, carbon } from '../../settings';
import { getDevtoolsProps } from '../../global/js/utils/devtools';
var componentName = 'InlineEditV2';
var blockClass = "".concat(pkg.prefix, "--inline-edit-v2");
export var InlineEditV2 = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _cx;

  var cancelLabel = _ref.cancelLabel,
      editLabel = _ref.editLabel,
      invalid = _ref.invalid,
      invalidLabel = _ref.invalidLabel,
      onCancel = _ref.onCancel,
      onChange = _ref.onChange,
      onSave = _ref.onSave,
      readOnly = _ref.readOnly,
      saveLabel = _ref.saveLabel,
      value = _ref.value,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      initialValue = _useState4[0],
      setInitialValue = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      dirtyInput = _useState6[0],
      setDirtyInput = _useState6[1];

  var inputRef = useRef(null);
  var canSave = value !== initialValue && !invalid;
  var escaping = useRef(false);
  useEffect(function () {
    if (!initialValue && !dirtyInput) {
      setInitialValue(value);
    }
  }, [initialValue, dirtyInput, value]);

  var isTargetingChild = function isTargetingChild(_ref2) {
    var currentTarget = _ref2.currentTarget,
        relatedTarget = _ref2.relatedTarget;
    return currentTarget.contains(relatedTarget);
  };

  var onChangeHandler = function onChangeHandler(_ref3) {
    var target = _ref3.target;

    if (!dirtyInput) {
      setDirtyInput(true);
    }

    onChange(target.value);
  };

  var onFocusHandler = function onFocusHandler(e) {
    if (readOnly) {
      return;
    }

    if (!isTargetingChild(e)) {
      inputRef.current.focus();
      setFocused(true);
    }
  };

  var onSaveHandler = function onSaveHandler() {
    setInitialValue(value);
    setFocused(false);
    setDirtyInput(false);
    onSave();
  };

  var onCancelHandler = function onCancelHandler() {
    setFocused(false);
    setDirtyInput(false);
    onCancel(initialValue);
  };

  var onBlurHandler = function onBlurHandler(e) {
    if (readOnly || escaping.current) {
      return;
    }

    if (!isTargetingChild(e)) {
      if (canSave) {
        onSaveHandler();
      } else {
        onCancelHandler();
      }
    }
  };

  var returnHandler = function returnHandler() {
    if (canSave) {
      onSaveHandler();
    }
  };

  var escapeHandler = function escapeHandler() {
    onCancelHandler();
  };

  var onKeyHandler = function onKeyHandler(e) {
    // to prevent blur handler from being called twice add additional state to check if escape is being used
    escaping.current = true;

    switch (e.key) {
      case 'Escape':
        inputRef.current.blur();
        escapeHandler();
        break;

      case 'Enter':
        inputRef.current.blur();
        returnHandler();
        break;

      default:
        break;
    }

    escaping.current = false;
  };

  return /*#__PURE__*/React.createElement("div", _extends({}, rest, {
    ref: ref
  }, getDevtoolsProps(componentName)), /*#__PURE__*/React.createElement("div", {
    className: cx(blockClass, (_cx = {}, _defineProperty(_cx, "".concat(blockClass, "-focused"), focused), _defineProperty(_cx, "".concat(blockClass, "-readonly"), readOnly), _cx)),
    onFocus: onFocusHandler,
    onBlur: onBlurHandler
  }, /*#__PURE__*/React.createElement("input", {
    className: cx("".concat(blockClass, "__text-input"), "".concat(carbon.prefix, "--text-input")),
    type: "text",
    value: value,
    onChange: onChangeHandler,
    ref: inputRef,
    readOnly: readOnly,
    onKeyDown: onKeyHandler
  }), focused ? /*#__PURE__*/React.createElement(React.Fragment, null, invalid && /*#__PURE__*/React.createElement(WarningFilled16, {
    className: "".concat(blockClass, "__warning-icon")
  }), /*#__PURE__*/React.createElement(Button, {
    hasIconOnly: true,
    renderIcon: Close24,
    size: "sm",
    iconDescription: cancelLabel,
    onClick: onCancelHandler,
    kind: "ghost",
    tabIndex: 0,
    key: "cancel",
    className: "".concat(blockClass, "__btn ").concat(blockClass, "__btn-cancel")
  }), canSave && /*#__PURE__*/React.createElement(Button, {
    hasIconOnly: true,
    renderIcon: Checkmark24,
    size: "sm",
    iconDescription: saveLabel,
    onClick: onSaveHandler,
    kind: "ghost",
    tabIndex: 0,
    key: "save",
    className: "".concat(blockClass, "__btn ").concat(blockClass, "__btn-save"),
    disabled: !canSave
  })) : /*#__PURE__*/React.createElement(Button, {
    className: "".concat(blockClass, "__btn ").concat(blockClass, "__btn-edit"),
    hasIconOnly: true,
    renderIcon: readOnly ? EditOff24 : Edit24,
    size: "sm",
    iconDescription: editLabel,
    onClick: onFocusHandler,
    kind: "ghost",
    disabled: readOnly,
    tabIndex: 0,
    key: "edit"
  })), focused && invalid && /*#__PURE__*/React.createElement("p", {
    className: "".concat(blockClass, "__warning-text")
  }, invalidLabel));
});
InlineEditV2 = pkg.checkComponentEnabled(InlineEditV2, componentName);
InlineEditV2.displayName = componentName;
InlineEditV2.propTypes = {
  /**
   * label for cancel button
   */
  cancelLabel: PropTypes.string.isRequired,

  /**
   * label for edit button
   */
  editLabel: PropTypes.string.isRequired,

  /**
   * determines if the input is invalid
   */
  invalid: PropTypes.bool,

  /**
   * text that is displayed if the input is invalid
   */
  invalidLabel: PropTypes.string,

  /**
   * handler that is called when the cancel button is pressed or when the user removes focus from the input and there is no new value
   */
  onCancel: PropTypes.func.isRequired,

  /**
   * handler that is called when the input is updated
   */
  onChange: PropTypes.func.isRequired,

  /**
   * handler that is called when the save button is pressed or when the user removes focus from the input if it has a new value
   */
  onSave: PropTypes.func.isRequired,

  /**
   * determines if the input is in readOnly mode
   */
  readOnly: PropTypes.bool,

  /**
   * label for save button
   */
  saveLabel: PropTypes.string.isRequired,

  /**
   * current value of the input
   */
  value: PropTypes.string.isRequired
};
InlineEditV2.defaultProps = {
  invalid: false,
  invalidLabel: '',
  readOnly: false
};